/**
 * ログインが切れたときの行き先（docs/features の 02 ログイン）。
 *
 * <b>ログインしていた人の要求が 401 になったら、切れたと分かる形でログイン画面へ送る</b>——
 * 何も言わずにログイン画面へ移ると、利用者には「急に画面が変わった」「入れたものが消えた」としか見えない。
 * 「ログインしていたか」はこのタブの sessionStorage に印を残して見分ける（ヘッダーがログインの状態を読んだとき付ける）。
 * 初めて来た人（印が無い）には理由を出さない。
 *
 * 操作が無いまま 24 時間で切れる（Pleasanter.net の /fs と同じ。API の <c>PortalCookieSetup.IdleTimeout</c>）。
 */

import { clearAuthHint } from "./auth-hint.js";

/** このタブでログインしていた印。 */
export const signedInMark = "portal.signedIn";

/** ログイン画面が理由を出す合図（<c>reason</c>）。 */
export const expiredReasons = {
    /** 画面を開いたとき（読むとき）に切れていた。 */
    viewing: "expired",
    /** 送ったときに切れていた（入力は送られていない）。 */
    sending: "expired-send",
} as const;

const storage = (): Storage | undefined => {
    try {
        return globalThis.sessionStorage;
    } catch {
        // 使えない（プライベートな窓など）なら、理由を出さないだけ
        return undefined;
    }
};

/** ログインしていた印を付ける／外す。 */
export function markSignedIn(signedIn: boolean): void {
    try {
        if (signedIn) {
            storage()?.setItem(signedInMark, "1");
        } else {
            storage()?.removeItem(signedInMark);
        }
    } catch {
        // 書けなくても画面は動かす
    }
}

/** このタブでログインしていたか。読めなければ「していない」（理由を出さない）。 */
const wasSignedInHere = (): boolean => {
    try {
        return storage()?.getItem(signedInMark) === "1";
    } catch {
        return false;
    }
};

/**
 * 401 を受けたときの行き先。<b>戻り先に今の画面を付ける</b>（ログインし直した後に戻れる）。
 * ログインしていたなら、切れた理由も付けて印を外す。
 */
export function loginUrlAfterUnauthorized(returnTo: string, sending: boolean): string {
    // 次のページのヘッダーをログイン中に見せない（app/auth-hint.ts）
    clearAuthHint();
    const params = new URLSearchParams({ returnUrl: returnTo });
    if (wasSignedInHere()) {
        params.set("reason", sending ? expiredReasons.sending : expiredReasons.viewing);
        markSignedIn(false);
    }
    return `/Account/Login?${params.toString()}`;
}

/**
 * 招待の承諾の画面で 401 を受けたときの行き先。<b>ログインではなく、合言葉を引き継いだアカウント登録
 * （<c>registerUrl</c>）へ送る</b>——招待メールのリンクを開く人には、まだアカウントを持たない人が多い。
 * アカウントのある人は、登録の画面の「ログイン」から招待のリンクへ戻る。ヘッダーの控えとログインしていた印はログインのときと同じく外す。
 */
export function registerUrlAfterUnauthorized(registerUrl: string): string {
    clearAuthHint();
    markSignedIn(false);
    return registerUrl;
}

/** ログイン画面に出す、切れた理由。理由が無ければ null。 */
export function expiredMessage(reason: string | null): string | null {
    switch (reason) {
        case expiredReasons.viewing: {
            return "一定時間操作がなかったため、ログアウトしました。お手数ですが、もう一度ログインしてください。";
        }
        case expiredReasons.sending: {
            return "一定時間操作がなかったため、ログアウトしました（入力した内容は送られていません）。"
                + "お手数ですが、もう一度ログインしてから、あらためて入力してください。";
        }
        default: {
            return null;
        }
    }
}
